/**
 * Deterministic NewtonWars-style simulation.
 * Ported from Draradech/NewtonWars simulation.c (MIT).
 * Independent of rendering and networking.
 */

import { DEFAULT_CONFIG, BATTLEFIELD_SIZE, SIM_DT, SIM_HZ } from './config.js';
import { FULL_CIRCLE_UNITS, uiToInternal, unitsToUiDegrees, velocityFromInternal } from './angle.js';
import { evaluateTrickshot, type TrickshotTier } from './trickshot.js';
import { clamp, distance, length, vadd, vdiv, vmul, vsub, type Vec2 } from './vector.js';

export interface Planet {
  position: Vec2;
  radius: number;
  mass: number;
}

export interface Missile {
  id: string;
  position: Vec2;
  speed: Vec2;
  acceleration: Vec2;
  live: boolean;
  leftSource: boolean;
  stale: boolean;
  ownerSlot: number;
  shotIndex: number;
  ageTicks: number;
  trail: Vec2[];
  isDebris: boolean;
}

export interface SimPlayer {
  slot: number;
  playerId: string;
  name: string;
  position: Vec2;
  /** Internal physics angle (degrees). */
  angleInternal: number;
  /** UI angle as integer micro-degree units. */
  angleUnits: number;
  velocity: number;
  energy: number;
  /** Single score value: +trickshot on kill, −1 on death. */
  points: number;
  active: boolean;
  connected: boolean;
  framesalive: number;
  currentShot: number;
  /** Pending fire flag (like original `valid`). */
  pendingFire: boolean;
  shots: (Missile | null)[];
}

export type SimMode = 'playing' | 'board';

export interface KillEvent {
  killerSlot: number;
  victimSlot: number;
  x: number;
  y: number;
  trickshot: TrickshotTier;
  points: number;
  flightMs: number;
}

export interface SimEvents {
  kills: KillEvent[];
  missileEnds: { id: string; reason: 'hit' | 'planet' | 'void' | 'maxlen' | 'replaced'; x: number; y: number }[];
  missileSpawns: Missile[];
  respawns: number[];
  planetImpacts: { x: number; y: number; missileId: string }[];
}

export interface SimConfig {
  battlefieldSize: number;
  numPlanets: number;
  numShots: number;
  energyRate: number;
  energyLimit: number;
  roundTimeSec: number;
  boardTimeSec: number;
  playerSize: number;
  margin: number;
  maxSegments: number;
  segmentSteps: number;
  numDebrisParticles: number;
  debrisSpeed: number;
  velocityMax: number;
  initialEnergy: number;
  defaultVelocity: number;
  maxPlayers: number;
}

function defaultSimConfig(maxPlayers: number): SimConfig {
  return {
    battlefieldSize: BATTLEFIELD_SIZE,
    numPlanets: DEFAULT_CONFIG.numPlanets,
    numShots: DEFAULT_CONFIG.numShots,
    energyRate: DEFAULT_CONFIG.energyRate,
    energyLimit: DEFAULT_CONFIG.energyLimit,
    roundTimeSec: DEFAULT_CONFIG.roundTimeSec,
    boardTimeSec: DEFAULT_CONFIG.boardTimeSec,
    playerSize: DEFAULT_CONFIG.playerSize,
    margin: DEFAULT_CONFIG.margin,
    maxSegments: DEFAULT_CONFIG.maxSegments,
    segmentSteps: DEFAULT_CONFIG.segmentSteps,
    numDebrisParticles: DEFAULT_CONFIG.numDebrisParticles,
    debrisSpeed: DEFAULT_CONFIG.debrisSpeed,
    velocityMax: DEFAULT_CONFIG.velocityMax,
    initialEnergy: DEFAULT_CONFIG.initialEnergy,
    defaultVelocity: DEFAULT_CONFIG.defaultVelocity,
    maxPlayers,
  };
}

let missileSeq = 0;
function nextMissileId(): string {
  missileSeq += 1;
  return `m${missileSeq}`;
}

export class Simulation {
  readonly conf: SimConfig;
  /** Dense trail history — needed for single-player rendering only. */
  readonly recordTrails: boolean;
  planets: Planet[] = [];
  players: SimPlayer[] = [];
  mode: SimMode = 'playing';
  timeRemainFrames: number;
  killFlash = 0;
  tick = 0;
  private pmin = 0;
  private pmax = 0;
  private potential: number[][] = [];
  private area: number[][] = [];
  private rng: () => number;

  constructor(
    maxPlayers: number,
    seed?: number,
    numPlanets?: number,
    options?: { recordTrails?: boolean },
  ) {
    this.conf = defaultSimConfig(maxPlayers);
    this.recordTrails = options?.recordTrails !== false;
    if (numPlanets !== undefined) {
      this.conf.numPlanets = clamp(Math.round(numPlanets), 10, 25);
    }
    this.timeRemainFrames = this.conf.roundTimeSec * 60;
    this.rng = mulberry32(seed ?? Date.now());
    for (let i = 0; i < maxPlayers; i++) {
      this.players.push(this.makeEmptyPlayer(i));
    }
    this.initPlanets();
  }

  private makeEmptyPlayer(slot: number): SimPlayer {
    return {
      slot,
      playerId: '',
      name: '',
      position: { x: 0, y: 0 },
      angleInternal: 90, // UI 0° (up)
      angleUnits: 0,
      velocity: this.conf.defaultVelocity,
      energy: this.conf.initialEnergy,
      points: 0,
      active: false,
      connected: false,
      framesalive: 0,
      currentShot: 0,
      pendingFire: false,
      shots: Array.from({ length: this.conf.numShots }, () => null),
    };
  }

  private rand(): number {
    return this.rng();
  }

  private calcGPot(pos: Vec2): number {
    let potential = 0;
    for (const planet of this.planets) {
      const l = distance(planet.position, pos);
      if (l <= planet.radius) return 0;
      potential += planet.mass / l;
    }
    return potential;
  }

  /** Iterative flood fill (original C is recursive; deep regions overflow JS stacks). */
  private floodfill(startI: number, startJ: number): void {
    const stack: [number, number][] = [[startI, startJ]];
    while (stack.length) {
      const [i, j] = stack.pop()!;
      if (i < 0 || j < 0 || i > 159 || j > 119) continue;
      if (this.area[i][j] || this.potential[i][j] <= this.pmin) continue;
      this.area[i][j] = 1;
      stack.push([i - 1, j], [i + 1, j], [i, j - 1], [i, j + 1]);
    }
  }

  private potentialEvaluation(): boolean {
    const W = this.conf.battlefieldSize;
    const H = this.conf.battlefieldSize;
    let sum = 0;
    let num = 0;
    this.potential = Array.from({ length: 160 }, () => Array(120).fill(0));
    this.area = Array.from({ length: 160 }, () => Array(120).fill(0));

    for (let i = 0; i < 160; i++) {
      const px = ((i - 20) * W) / 120;
      for (let j = 0; j < 120; j++) {
        const py = ((j - 20) * H) / 80;
        const pot = this.calcGPot({ x: px, y: py });
        this.potential[i][j] = pot;
        if (pot > 0.1) {
          sum += pot;
          num++;
        }
      }
    }

    this.pmin = num > 0 ? (0.9 * sum) / num : 1;
    this.pmax = this.pmin + 40;

    let foundI = 0;
    let foundJ = 0;
    let found = false;
    for (let i = 0; i < 160 && !found; i++) {
      for (let j = 0; j < 120 && !found; j++) {
        if (this.potential[i][j] > this.pmin) {
          found = true;
          foundI = i;
          foundJ = j;
        }
      }
    }
    if (!found) return false;
    this.floodfill(foundI, foundJ);

    for (let i = 0; i < 160; i++) {
      for (let j = 0; j < 120; j++) {
        if (!this.area[i][j] && this.potential[i][j] > this.pmin) return false;
      }
    }
    return true;
  }

  private initPlanets(): void {
    const size = this.conf.battlefieldSize;
    let tries = 0;
    do {
      this.planets = [];
      for (let i = 0; i < this.conf.numPlanets; i++) {
        let nok: boolean;
        let planet: Planet;
        do {
          const radius = 20 + this.rand() * 20;
          const mass = (radius * radius * radius) / 10;
          planet = {
            radius,
            mass,
            position: {
              x: radius + this.rand() * (size - radius * 2),
              y: radius + this.rand() * (size - radius * 2),
            },
          };
          nok = false;
          for (let j = 0; j < this.planets.length; j++) {
            if (distance(planet.position, this.planets[j].position) <= planet.radius + this.planets[j].radius) {
              nok = true;
              break;
            }
          }
        } while (nok);
        this.planets.push(planet);
      }
      tries++;
      if (tries > 80) break;
    } while (!this.potentialEvaluation());
  }

  private acc(pos: Vec2): Vec2 {
    let a: Vec2 = { x: 0, y: 0 };
    for (const planet of this.planets) {
      let an = vsub(planet.position, pos);
      const l = length(an);
      if (l < 1e-9) continue;
      an = vdiv(an, l);
      an = vmul(an, planet.mass / (l * l));
      a = vadd(a, an);
    }
    return a;
  }

  private stepIntegrate(m: Missile): void {
    const dt = 1 / this.conf.segmentSteps;
    const newPos = vadd(vadd(m.position, vmul(m.speed, dt)), vmul(m.acceleration, 0.5 * dt * dt));
    const newAcc = this.acc(newPos);
    const newSpeed = vadd(m.speed, vmul(vadd(m.acceleration, newAcc), 0.5 * dt));
    m.position = newPos;
    m.speed = newSpeed;
    m.acceleration = newAcc;
  }

  activatePlayer(slot: number, playerId: string, name: string, clearScores: boolean): void {
    const p = this.players[slot];
    p.playerId = playerId;
    p.name = name.slice(0, 12);
    p.active = true;
    p.connected = true;
    this.initPlayer(slot, clearScores);
  }

  deactivatePlayer(slot: number): void {
    const p = this.players[slot];
    p.active = false;
    p.connected = false;
    p.pendingFire = false;
    for (const s of p.shots) {
      if (s) s.live = false;
    }
    this.killFlash = 1;
  }

  setConnected(slot: number, connected: boolean): void {
    this.players[slot].connected = connected;
  }

  setName(slot: number, name: string): void {
    this.players[slot].name = name.slice(0, 12);
  }

  setAngleUnits(slot: number, units: number): void {
    const p = this.players[slot];
    const FULL = FULL_CIRCLE_UNITS;
    const u = ((Math.round(units) % FULL) + FULL) % FULL;
    p.angleUnits = u;
    const uiDeg = unitsToUiDegrees(u);
    p.angleInternal = uiToInternal(uiDeg);
  }

  setVelocity(slot: number, v: number): void {
    this.players[slot].velocity = clamp(v, 0, this.conf.velocityMax);
  }

  /** Queue a fire like original updateAngle(..., ce) setting valid=1. */
  requestFire(slot: number): boolean {
    const p = this.players[slot];
    if (!p.active || this.mode !== 'playing') return false;
    if (p.energy < p.velocity) return false;
    p.pendingFire = true;
    return true;
  }

  private initPlayer(slot: number, clear: boolean, avoidPos?: Vec2): void {
    const p = this.players[slot];
    const size = this.conf.battlefieldSize;

    if (clear) {
      p.angleUnits = 0;
      p.angleInternal = uiToInternal(0);
      p.velocity = this.conf.defaultVelocity;
      p.points = 0;
      p.currentShot = 0;
    }

    p.energy = this.conf.initialEnergy;
    p.framesalive = 0;
    p.pendingFire = false;

    for (let i = 0; i < this.conf.numShots; i++) {
      const s = p.shots[i];
      if (clear || !s?.live) {
        p.shots[i] = null;
      } else if (s) {
        s.stale = true;
      }
    }

    p.position = this.pickSpawnPosition(slot, avoidPos, size);
  }

  /** Valid spawn: gravity band, clear of planets & other players, away from edges. */
  private spawnEdgeMargin(): number {
    // Leave room for player name labels drawn above the ship
    return Math.max(70, this.conf.battlefieldSize * 0.05);
  }

  private randomInfieldPos(size: number): Vec2 {
    const m = this.spawnEdgeMargin();
    const span = Math.max(1, size - 2 * m);
    return { x: m + this.rand() * span, y: m + this.rand() * span };
  }

  private isValidSpawn(slot: number, pos: Vec2): boolean {
    const size = this.conf.battlefieldSize;
    const m = this.spawnEdgeMargin();
    if (pos.x < m || pos.y < m || pos.x > size - m || pos.y > size - m) return false;
    const pot = this.calcGPot(pos);
    if (pot > this.pmax || pot < this.pmin) return false;
    for (const other of this.players) {
      if (other.slot === slot || !other.active) continue;
      if (distance(pos, other.position) <= 200 + this.conf.playerSize * 2) return false;
    }
    for (const planet of this.planets) {
      if (distance(pos, planet.position) <= planet.radius + this.conf.playerSize) return false;
    }
    return true;
  }

  /**
   * On death respawn (avoidPos set): pick a random valid spot near ~50% of
   * battlefield size away (not the farthest — that ping-pongs between poles).
   * Initial placement: first valid spot.
   */
  private pickSpawnPosition(slot: number, avoidPos: Vec2 | undefined, size: number): Vec2 {
    if (!avoidPos) {
      for (let guard = 0; guard < 500; guard++) {
        const pos = this.randomInfieldPos(size);
        if (this.isValidSpawn(slot, pos)) return pos;
      }
      return this.randomInfieldPos(size);
    }

    const targetDist = size * 0.5;
    const band = size * 0.12; // accept roughly 38%–62% of field size
    const good: Vec2[] = [];
    const fallback: { pos: Vec2; score: number }[] = [];

    for (let guard = 0; guard < 500; guard++) {
      const pos = this.randomInfieldPos(size);
      if (!this.isValidSpawn(slot, pos)) continue;
      const d = distance(pos, avoidPos);
      if (Math.abs(d - targetDist) <= band) {
        good.push(pos);
        if (good.length >= 24) break;
      } else {
        fallback.push({ pos, score: Math.abs(d - targetDist) });
      }
    }

    if (good.length > 0) {
      return good[Math.floor(this.rand() * good.length)];
    }
    if (fallback.length > 0) {
      fallback.sort((a, b) => a.score - b.score);
      const top = fallback.slice(0, Math.min(12, fallback.length));
      return top[Math.floor(this.rand() * top.length)].pos;
    }
    return this.randomInfieldPos(size);
  }

  private spawnMissile(
    ownerSlot: number,
    pos: Vec2,
    speed: Vec2,
    isDebris: boolean,
    events: SimEvents,
  ): Missile {
    const p = this.players[ownerSlot];
    p.currentShot = (p.currentShot + 1) % this.conf.numShots;
    const shotIndex = p.currentShot;
    const old = p.shots[shotIndex];
    if (old?.live) {
      events.missileEnds.push({
        id: old.id,
        reason: 'replaced',
        x: old.position.x,
        y: old.position.y,
      });
    }

    const m: Missile = {
      id: nextMissileId(),
      position: { ...pos },
      speed: { ...speed },
      acceleration: this.acc(pos),
      live: true,
      // Original: debris also starts with leftSource=0 and must leave the blast site
      leftSource: false,
      stale: false,
      ownerSlot,
      shotIndex,
      ageTicks: 0,
      trail: this.recordTrails ? [{ ...pos }] : [],
      isDebris,
    };
    p.shots[shotIndex] = m;
    events.missileSpawns.push(m);
    return m;
  }

  private initShot(slot: number, events: SimEvents): void {
    const p = this.players[slot];
    p.energy -= p.velocity;
    const vel = velocityFromInternal(p.angleInternal, p.velocity);
    this.spawnMissile(slot, p.position, vel, false, events);
  }

  private spawnDebris(slot: number, pos: Vec2, events: SimEvents): void {
    const n = this.conf.numDebrisParticles;
    for (let i = 0; i < n; i++) {
      // Even spread + small jitter (matches original random feel, avoids clumps)
      const angle = (i / n) * 360 + this.rand() * (360 / n) * 0.35;
      const rad = (angle / 180) * Math.PI;
      const speed = {
        x: this.conf.debrisSpeed * Math.cos(rad),
        y: this.conf.debrisSpeed * -Math.sin(rad),
      };
      this.spawnMissile(slot, pos, speed, true, events);
    }
  }

  private missileEnd(
    m: Missile,
    reason: SimEvents['missileEnds'][0]['reason'],
    events: SimEvents,
  ): void {
    m.live = false;
    events.missileEnds.push({ id: m.id, reason, x: m.position.x, y: m.position.y });
    m.trail = [];
    if (m.stale) {
      const p = this.players[m.ownerSlot];
      if (p.shots[m.shotIndex]?.id === m.id) p.shots[m.shotIndex] = null;
    }
  }

  /** Snap missile onto planet surface so the trail reaches the impact. */
  private impactOnPlanet(m: Missile, planet: Planet, events: SimEvents): void {
    const dx = m.position.x - planet.position.x;
    const dy = m.position.y - planet.position.y;
    const len = Math.hypot(dx, dy) || 1;
    m.position = {
      x: planet.position.x + (dx / len) * planet.radius,
      y: planet.position.y + (dy / len) * planet.radius,
    };
    if (this.recordTrails) m.trail.push({ ...m.position });
    events.planetImpacts.push({ x: m.position.x, y: m.position.y, missileId: m.id });
    this.missileEnd(m, 'planet', events);
  }

  private playerHit(m: Missile, killerSlot: number, victimSlot: number, events: SimEvents): void {
    const killer = this.players[killerSlot];
    const victim = this.players[victimSlot];

    const flightSec = m.isDebris ? 0 : m.ageTicks / SIM_HZ;
    const flightMs = Math.round(flightSec * 1000);
    let trickshot: TrickshotTier = 'none';
    let basePoints = 1;
    if (!m.isDebris && killerSlot !== victimSlot) {
      const evaled = evaluateTrickshot(flightSec);
      trickshot = evaled.tier;
      basePoints = evaled.points;
    }

    const awarded = killerSlot === victimSlot ? 0 : basePoints;

    if (killerSlot !== victimSlot) {
      killer.points += awarded;
      killer.energy += victim.energy / 2;
    }
    victim.points -= 1;

    this.missileEnd(m, 'hit', events);
    const oldPos = { ...victim.position };
    this.initPlayer(victimSlot, false, oldPos);
    this.spawnDebris(victimSlot, oldPos, events);
    events.kills.push({
      killerSlot,
      victimSlot,
      x: oldPos.x,
      y: oldPos.y,
      trickshot,
      points: awarded,
      flightMs,
    });
    events.respawns.push(victimSlot);
    this.killFlash = 1;
  }

  private simulateMissiles(events: SimEvents): void {
    const size = this.conf.battlefieldSize;
    const margin = this.conf.margin;

    for (const p of this.players) {
      if (!p.active) continue;
      for (const m of p.shots) {
        if (!m?.live) continue;

        for (let step = 0; step < this.conf.segmentSteps; step++) {
          if (!m.live) break;
          this.stepIntegrate(m);

          for (const planet of this.planets) {
            if (distance(planet.position, m.position) <= planet.radius) {
              this.impactOnPlanet(m, planet, events);
              break;
            }
          }
          if (!m.live) break;

          for (const target of this.players) {
            if (!target.active) continue;
            const l = distance(target.position, m.position);
            if (l <= this.conf.playerSize && m.leftSource) {
              this.playerHit(m, p.slot, target.slot, events);
              break;
            }
            if (l > this.conf.playerSize + 1 && target.slot === p.slot) {
              m.leftSource = true;
            }
          }
          if (!m.live) break;

          if (
            m.position.x < -margin ||
            m.position.x > size + margin ||
            m.position.y < -margin ||
            m.position.y > size + margin
          ) {
            this.missileEnd(m, 'void', events);
            break;
          }

          // Dense trail for SP rendering only (MP server skips — was a major RAM sink)
          if (this.recordTrails) m.trail.push({ ...m.position });
        }

        if (m.live) {
          m.ageTicks++;
          if (m.ageTicks >= this.conf.maxSegments) {
            // Same FX as planet impact, then destroy
            events.planetImpacts.push({
              x: m.position.x,
              y: m.position.y,
              missileId: m.id,
            });
            this.missileEnd(m, 'maxlen', events);
          }
          // Do not splice live trails — dropping the head recreates the Snake effect
          // and desyncs the SP trail cursor. Maxlen already bounds lifetime.
        }
      }
    }
  }

  /** Enter scoreboard: wipe shots, new planet layout (scores kept for display). */
  private enterBoard(): void {
    for (const p of this.players) {
      for (const m of p.shots) {
        if (m) m.live = false;
      }
      for (let i = 0; i < p.shots.length; i++) p.shots[i] = null;
      p.pendingFire = false;
    }
    this.initPlanets();
    this.mode = 'board';
    this.timeRemainFrames = this.conf.boardTimeSec * 60;
    this.killFlash = 0;
  }

  /** Start next round after scoreboard: fresh scores + spawn positions. */
  reinitialize(): void {
    for (const p of this.players) {
      if (p.active) this.initPlayer(p.slot, true);
    }
    this.mode = 'playing';
    this.timeRemainFrames = this.conf.roundTimeSec * 60;
    this.killFlash = 0;
  }

  step(): SimEvents {
    const events: SimEvents = {
      kills: [],
      missileEnds: [],
      missileSpawns: [],
      respawns: [],
      planetImpacts: [],
    };
    this.tick++;

    for (const p of this.players) {
      if (!p.active) continue;

      if (p.pendingFire && p.energy >= p.velocity && this.mode === 'playing') {
        this.initShot(p.slot, events);
        p.pendingFire = false;
      } else if (p.pendingFire && p.energy < p.velocity) {
        p.pendingFire = false;
      }

      // Original: energy += rate / 60 per frame (~60 Hz)
      p.energy += this.conf.energyRate * SIM_DT;
      if (p.energy > this.conf.energyLimit) p.energy = this.conf.energyLimit;

      if (p.active) p.framesalive++;
    }

    if (this.mode === 'playing') {
      this.simulateMissiles(events);
      this.killFlash *= 0.95;
    }

    this.timeRemainFrames--;

    if (this.timeRemainFrames <= 0) {
      if (this.mode === 'playing') {
        this.enterBoard();
      } else {
        this.reinitialize();
      }
    }

    return events;
  }

  liveMissiles(): Missile[] {
    const out: Missile[] = [];
    for (const p of this.players) {
      if (!p.active) continue;
      for (const m of p.shots) {
        if (m?.live) out.push(m);
      }
    }
    return out;
  }

  timeRemainSec(): number {
    return Math.max(0, this.timeRemainFrames / 60);
  }
}

function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
